import { HttpClient, HttpContext } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, resource } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { IGNORE_SPACE_CONTEXT, owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { heicToJpeg } from '@modules/common/utils/heic-to-jpeg';
import { File } from '@modules/files/types/file';
import { hasFileLink } from '@modules/files/types/file-link';
import { isHeicImage, isMarkdownFile, isTiffImage, resolvePreviewType } from '@sharedlib/common/utils/preview-type';
import { Observable, firstValueFrom, fromEvent, takeUntil } from 'rxjs';
import { PDFPreviewComponent } from './pdf-preview/pdf-preview.component';
import { PresentationPreviewComponent } from './presentation-preview/presentation-preview.component';
import { PreviewErrorComponent } from './preview-error/preview-error.component';
import { SpreadsheetPreviewComponent } from './spreadsheet-preview/spreadsheet-preview.component';
import { TextPreviewComponent } from './text-preview/text-preview.component';
import { tiffToPng } from './tiff-to-png';
import { UnsupportedPreviewComponent } from './unsupported-preview/unsupported-preview.component';
import { WordPreviewComponent } from './word-preview/word-preview.component';
import { TopProgressService } from '@modules/top-progress/top-progress.service';
import { TopProgressTaskComponent } from '@modules/top-progress/top-progress-task.component';

interface LoadedContent {
  readonly blob: Blob;
  readonly fileUrl: SafeUrl;
  readonly fileUrlString: string;
}

@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LoadingIndicatorComponent,
    PDFPreviewComponent,
    TextPreviewComponent,
    WordPreviewComponent,
    SpreadsheetPreviewComponent,
    PresentationPreviewComponent,
    UnsupportedPreviewComponent,
    PreviewErrorComponent,
    TopProgressTaskComponent,
  ],
})
export class FilePreviewComponent {
  public readonly file = input(new File());
  protected readonly linkFile = computed(() => hasFileLink(this.file()));

  protected readonly previewType = computed(() => resolvePreviewType(this.file()));
  protected readonly renderAsMarkdown = computed(() => isMarkdownFile(this.file()));

  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly topProgress = inject(TopProgressService);
  private lastObjectUrl: string | null = null;

  protected readonly content = resource<LoadedContent | null, File>({
    // Skip the loader entirely for unsupported types — the template shows the
    // unsupported-preview card before any HTTP call would have been made.
    params: () => (this.linkFile() || this.previewType() === 'unsupported' ? undefined : this.file()),
    defaultValue: null,
    loader: async ({ params: file, abortSignal }) => {
      const signedUrl =
        file.sharedUrl ??
        (
          await firstValueFromAbortable(
            // The bytes are addressed by the FILE'S OWNER, never by the workspace on screen: the
            // object key is `<owner>/<checksum>`, so a request stamped with the ambient space asks
            // for a key nothing was ever written to and the signed URL 404s `NoSuchKey` (#1072).
            this.http.get<{ signedUrl: string }>(file.getSignedUrl(), {
              context: owningTenantContext(file.accountId),
            }),
            abortSignal,
          )
        ).signedUrl;
      const blob = await firstValueFromAbortable(
        // An absolute, presigned object-store URL must reach the store untouched. SigV4
        // canonicalises every query parameter except the signature, so the interceptor appending
        // `&space_id=` to it would produce `SignatureDoesNotMatch` — the failure that surfaces the
        // moment the 404 above is fixed.
        this.http.get(signedUrl, {
          responseType: 'blob',
          context: new HttpContext().set(IGNORE_SPACE_CONTEXT, true),
        }),
        abortSignal,
      );

      // Only Safari draws HEIC and TIFF, so a phone photo is shown as the JPEG it converts to, and a scan or fax as the PNG
      // of its first page; the original bytes stay in `blob`.
      const displayed = isHeicImage(file)
        ? await this.topProgress.track(heicToJpeg(blob, HEIC_PREVIEW_JPEG_QUALITY))
        : isTiffImage(file)
          ? await this.topProgress.track(tiffToPng(blob))
          : blob;
      abortSignal.throwIfAborted();

      this.revokeLastObjectUrl();
      const fileUrlString = URL.createObjectURL(displayed);
      this.lastObjectUrl = fileUrlString;
      return {
        blob,
        fileUrl: this.sanitizer.bypassSecurityTrustUrl(fileUrlString),
        fileUrlString,
      };
    },
  });

  protected readonly blob = computed(() => this.content.value()?.blob ?? null);
  protected readonly fileUrl = computed(() => this.content.value()?.fileUrl ?? null);
  protected readonly fileUrlString = computed(() => this.content.value()?.fileUrlString ?? null);
  protected readonly loading = this.content.isLoading;
  protected readonly error = this.content.error;

  public constructor() {
    inject(DestroyRef).onDestroy(() => this.revokeLastObjectUrl());
  }

  private revokeLastObjectUrl(): void {
    if (this.lastObjectUrl) {
      URL.revokeObjectURL(this.lastObjectUrl);
      this.lastObjectUrl = null;
    }
  }
}

const HEIC_PREVIEW_JPEG_QUALITY = 0.9;

async function firstValueFromAbortable<T>(source$: Observable<T>, signal: AbortSignal): Promise<T> {
  signal.throwIfAborted();
  try {
    return await firstValueFrom(source$.pipe(takeUntil(fromEvent(signal, 'abort'))));
  } catch (err) {
    signal.throwIfAborted();
    throw err;
  }
}
